/**
 * FishDetailPanel — Expanded detail view for a selected fish stack
 * Shows full stats, protection toggle, and market navigation
 */

import { FishStack, Quality, QualityColors, QualityNames } from '@/types';
import { getFishDefinition, QualityMultipliers } from '@/data';
import { useGameStore } from '@/state';

interface FishDetailPanelProps {
  stack: FishStack;
  onClose: () => void;
  onNavigateToMarket: () => void;
}

export function FishDetailPanel({ stack, onClose, onNavigateToMarket }: FishDetailPanelProps) {
  const definition = getFishDefinition(stack.definitionId);
  const quality = stack.quality as Quality;
  const multiplier = QualityMultipliers[quality];
  const perFishValue = Math.round(definition.baseValue * multiplier);
  const totalValue = perFishValue * stack.count;

  const protectedFish = useGameStore((state) => state.playerState.autoSellSettings.protectedFish);
  const toggleFishProtection = useGameStore((state) => state.toggleFishProtection);
  const isProtected = protectedFish[stack.definitionId]?.[quality] === true;

  const handleToggleProtection = () => {
    toggleFishProtection(stack.definitionId, quality);
  };

  return (
    <div className="col-span-full bg-gray-700/50 rounded-lg p-3 border border-gray-600 space-y-2">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">{definition.emoji || '🐟'}</span>
          <span className="text-sm font-bold text-white">{definition.name}</span>
          <span className="text-sm" style={{ color: QualityColors[quality] }}>
            {QualityNames[quality]}
          </span>
          <span className="text-sm text-gray-400">×{stack.count}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close detail panel"
          className="text-gray-400 hover:text-white text-sm px-2 py-1 rounded hover:bg-gray-600 transition-colors"
        >
          ✕
        </button>
      </div>

      {/* Value breakdown */}
      <div className="text-xs text-gray-300 space-y-1">
        <div className="flex items-center gap-4">
          <span>
            Base: <span className="text-yellow-400">{definition.baseValue}g</span>
          </span>
          <span>
            Multiplier: <span className="text-blue-400">×{multiplier}</span>
          </span>
          <span>
            Per fish: <span className="text-yellow-400">{perFishValue}g</span>
          </span>
          <span>
            Total: <span className="text-yellow-300 font-bold">{totalValue}g</span>
          </span>
        </div>
        <div className="text-gray-500">
          Species rarity: {definition.rarity.charAt(0).toUpperCase() + definition.rarity.slice(1)}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={handleToggleProtection}
          className={`text-xs px-3 py-1.5 rounded font-semibold transition-all ${
            isProtected
              ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
              : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
          }`}
        >
          {isProtected ? '🔒 Protected' : '🔓 Unprotected'}
        </button>
        <button
          type="button"
          onClick={onNavigateToMarket}
          className="text-xs bg-green-600/80 hover:bg-green-600 text-white px-3 py-1.5 rounded font-semibold transition-colors"
        >
          Sell at Market
        </button>
      </div>
    </div>
  );
}
